import type { InterventionStatus } from '@arhs-fleetback/types/entities/interventions';

import { STATUS_URL } from 'constants/ApiConstants';
import { ANSWERED, INTERVENTIONS_API_URL, OPEN, READ, SENT, TO_BE_SENT } from 'constants/InterventionConstants';
import type { Action, ErrorAction, GetRequest } from 'modules/global/generic.types';
import type { ModeEnum } from 'modules/global/typing/enums/modes.enum';
import type { ModuleEnum } from 'modules/global/typing/enums/module.enum';
import * as actionTypes from 'modules/interventions/actionTypes';

import type * as types from '../types/actions';
import type { InterventionsFilterRequest } from '../types/requests/interventions-filter-request';

type InitDashboardPayload = { mode: ModeEnum; module: ModuleEnum };

type SetSortPayload = { type: string; direction: string; withEmptyDate?: boolean };

/**
 * Params sent by the "select" filter query. This is a narrower/different shape than the
 * BE `InterventionsFilterRequest` mirror (array `dealerIds`, numeric `size`/`page`,
 * `startDateFrom`/`startDateTo`, `fields`), so it is typed locally rather than with the mirror.
 * `mode` selects the mode-scoped endpoint from `INTERVENTIONS_API_URL`.
 */
type FetchInterventionsSelectParams = InterventionsFilterRequest & { mode: ModeEnum };

// LIFE CYCLE

export const initDashboard = (mode: ModeEnum, module: ModuleEnum, meta: Record<string, unknown> = {}): Action<InitDashboardPayload> => ({
  type: actionTypes.INIT_DASHBOARD,
  payload: { mode, module },
  meta,
});

export const startFetchPolling = (): Action<undefined> => ({
  type: actionTypes.FETCH_INTERVENTIONS_START_POLLING,
});

export const stopFetchPolling = (): Action<undefined> => ({
  type: actionTypes.FETCH_INTERVENTIONS_STOP_POLLING,
});

// MODAL

export const openCreateInterventionModal = (payload?: unknown): Action<unknown> => ({
  type: actionTypes.OPEN_CREATE_INTERVENTION_MODAL,
  payload,
});

export const closeCreateInterventionModal = (payload?: unknown): Action<unknown> => ({
  type: actionTypes.CLOSE_CREATE_INTERVENTION_MODAL,
  payload,
});

// UI STATE

export const setSchedulableType = (type: types.SchedulableTypeEnum, meta: Record<string, unknown> = {}): types.SetSchedulableTypeAction => ({
  type: actionTypes.SET_SCHEDULABLE_TYPE,
  payload: type,
  meta,
});

export const setSort = (payload: SetSortPayload, meta: Record<string, unknown> = {}): Action<SetSortPayload> => ({
  type: actionTypes.SET_SORT,
  payload,
  meta,
});

export const fetchInterventionsStatus = (meta: Record<string, unknown> = {}): Action<GetRequest<undefined>> => ({
  type: actionTypes.FETCH_INTERVENTIONS_STATUS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: STATUS_URL
    }
  },
  meta: { '@app-request': true, ...meta },
});

export const fetchInterventionsStatusFailure = (error: string, meta: Record<string, unknown> = {}): ErrorAction => ({
  type: actionTypes.FETCH_INTERVENTIONS_STATUS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchInterventionsStatusSuccess = (statuses: InterventionStatus[], meta: Record<string, unknown> = {}): Action<InterventionStatus[]> => ({
  type: actionTypes.FETCH_INTERVENTIONS_STATUS_SUCCESS,
  payload: statuses,
  meta
});

export const fetchInterventionsSelect = (
  params: FetchInterventionsSelectParams,
  meta: Record<string, unknown> = {},
): Action<GetRequest<InterventionsFilterRequest>> => ({
  type: actionTypes.FETCH_INTERVENTIONS_SELECT_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[params.mode as keyof typeof INTERVENTIONS_API_URL]}/filter`,
      params: {
        status: [OPEN, TO_BE_SENT, SENT, READ, ANSWERED],
        size: 10,
        sort: 'startDate,desc',
        fields: 'id,referenceId,startDate,mode,module',
        ...params,
      },
    },
  },
  meta: { '@app-request': true, ...meta },
});

export const clearInterventionsSelect = (meta: Record<string, unknown> = {}): Action<undefined> => ({
  type: actionTypes.CLEAR_INTERVENTIONS_SELECT,
  meta,
});
